import React, { useEffect, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import AdditionalSessionFields from './additionalSessionFields';
import type {
  AdditionalSessionFieldRowData,
  SessionFields,
  FacilityFields,
  FieldTypes,
} from '../../API/services/IntakeFormService';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomDialog from '@ui-components/Dialog/CustomDialog';
import CustomDialogContent from '@ui-components/Dialog/CustomDialogContent';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomDialogActions from '@ui-components/Dialog/CustomDialogActions';
import SessionFieldsComponent from './sessionFields';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import './intake.css';
import FacilityFieldsComponent from './facilityFields';
import type { FacilityFieldRowData } from './facilityFields';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useGetIntakeFormMetaData,
  useGetIntakeFormListForddl,
  useSaveIntakeForm,
  useGetIntakeFormById,
} from '../../API/hooks/IntakeFormHook';
import BackIcon from '@assets/images/BackIcon.svg';

import CustomAutoCompleteSingleSelection from '@ui-components/AutoComplete/CustomAutoCompleteSingleSelection';
import type { Option } from '@ui-components/AutoComplete/CustomAutoCompleteSingleSelection';
import {
  INTAKE_ROUTE_PATH,
  NEW_INTAKE_ROUTE_PATH,
} from '../../routers/routePaths';
import CustomIconButton from '@qler-view/shared_lib/src/shared/lib/ui-components/IconButton/CustomIconButton';

const initialState = {
  intakeFormId: 0,
  intakeFormName: '',
  intakeFormBusinessId: '',
  sessionFields: [] as SessionFields[],
  additionalSessionFields: [] as AdditionalSessionFieldRowData[],
  facilityFields: [] as FacilityFieldRowData[],
  additionalSessionFieldsTypeddl: [] as FieldTypes[],
  facilityFieldsddl: [] as FacilityFields[],
};

const validationSchemaForddl = Yup.object().shape({
  selectedIntakeForm: Yup.object()
    .nullable()
    .required('Intake Form selection is required'),
});

const validationSchema = Yup.object().shape({
  intakeFormName: Yup.string().required('Intake Form Name is required'),
  sessionFields: Yup.array().of(
    Yup.object().shape({
      fieldValues: Yup.string().when('fieldName', {
        is: (fieldName: string) => fieldName === 'ED/IP',
        then: (schema) =>
          schema.required('Field Values are required for ED/IP'),
        otherwise: (schema) => schema.optional(),
      }),
    })
  ),
  additionalSessionFields: Yup.array().of(
    Yup.object().shape({
      fieldName: Yup.string().required('Field Name is required'),

      fieldType: Yup.number()
        .required('Field Type is required')
        .min(1, 'Field Type is required'),

      fieldValue: Yup.string().test(
        'fieldValue-required',
        'Field Value is required',
        function (value) {
          const { fieldType } = this.parent;
          const formValues = this.options.context as typeof initialState;
          const selectedFieldType =
            formValues?.additionalSessionFieldsTypeddl?.find(
              (type) => type.id === fieldType
            );
          const fieldTypeName = selectedFieldType?.name?.toLowerCase();

          if (fieldTypeName === 'dropdown' || fieldTypeName === 'checkbox') {
            return !!value && value.trim() !== '';
          }
          return true;
        }
      ),
    })
  ),
});

const IntakeForm: React.FC = () => {
  const { intakeFormId } = useParams<{ intakeFormId?: string }>();
  const location = useLocation();
  const { loadForm } = location.state || {};
  console.log('loadForm:', loadForm);
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId')?.toString() ?? '';
  const [formState, setFormState] = useState(initialState);
  const [openIntakeForm, setOpenIntakeForm] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const [selectedIntakeForm, setSelectedIntakeForm] = useState<Option | null>(
    null
  );

  const [formSubmitAction, setFormSubmitAction] = useState<string>('');

  const [intakeFormOptions, setIntakeFormOptions] = useState<Option[]>([]);

  const { IntakeFormMetaData, isLoading } = useGetIntakeFormMetaData(userId);

  const { IntakeFormListDdl, refetch: refetchIntakeFormList } =
    useGetIntakeFormListForddl(userId);

  const onSuccess = () => {
    if ((intakeFormId && loadForm === 'copy') || !intakeFormId) {
      navigate(`${INTAKE_ROUTE_PATH}?toast=success`);
    } else if (intakeFormId && !loadForm) {
      navigate(`${INTAKE_ROUTE_PATH}?toast=update`);
    }
  };

  const { mutate: saveIntakeForm, isPending: isSaving } =
    useSaveIntakeForm(onSuccess);

  const { IntakeFormById, isLoading: isLoadingById } = useGetIntakeFormById(
    userId,
    Number(intakeFormId)
  );

  useEffect(() => {
    if (IntakeFormById) {
      const baseFormState = {
        intakeFormName: IntakeFormById.intakeFormName,
        intakeFormBusinessId: IntakeFormById.intakeFormBusinessId,
        sessionFields: IntakeFormById.sessionFields,
        additionalSessionFields: IntakeFormById.additionalSessionFields,
        facilityFields: IntakeFormById.facilityFields,
        additionalSessionFieldsTypeddl:
          formState.additionalSessionFieldsTypeddl,
        facilityFieldsddl: formState.facilityFieldsddl,
      };

      if (!loadForm) {
        setFormState({
          ...baseFormState,
          intakeFormId: IntakeFormById.intakeFormId,
        });
      } else if (loadForm === 'copy') {
        setFormState({
          ...baseFormState,
          intakeFormId: 0,
        });
      }
    }
  }, [IntakeFormById, loadForm]);

  useEffect(() => {
    if (IntakeFormMetaData) {
      setFormState((prevState) => {
        if (IntakeFormById) {
          return {
            ...prevState,
            additionalSessionFieldsTypeddl: IntakeFormMetaData.fieldTypes || [],
            facilityFieldsddl: IntakeFormMetaData.facilityFields || [],
          };
        }

        return {
          ...prevState,
          sessionFields: IntakeFormMetaData.sessionFields || [],
          additionalSessionFieldsTypeddl: IntakeFormMetaData.fieldTypes || [],
          facilityFieldsddl: IntakeFormMetaData.facilityFields || [],
        };
      });
    }
  }, [IntakeFormMetaData, IntakeFormById]);

  useEffect(() => {
    if (IntakeFormListDdl) {
      setIntakeFormOptions(IntakeFormListDdl);
    }
  }, [IntakeFormListDdl]);

  const handleIntakeformSave = async (intakeData: typeof formState) => {
    const { ...payload } = intakeData;
    if (formSubmitAction) {
      saveIntakeForm({ userId, action: formSubmitAction, ...payload });
    }
  };

  const handleOpenIntakeForm = (state: boolean) => {
    setOpenIntakeForm(state);
  };

  const handleOpenConfirmDialog = (state: boolean) => {
    setOpenConfirmDialog(state);
  };

  const handleFetchIntakeFormList = () => {
    refetchIntakeFormList();
  };

  const handleIntakeFormChange = (newValue: Option | null) => {
    setSelectedIntakeForm(newValue);
  };

  let buttonText = '';

  const isCopyOrNew = (intakeFormId && loadForm === 'copy') || !intakeFormId;

  if (isCopyOrNew) {
    buttonText = isSaving ? 'Saving...' : 'Save';
  } else {
    buttonText = isSaving ? 'Updating...' : 'Update';
  }

  if (isLoading || isLoadingById) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  return (
    <Box>
      <Formik
        initialValues={formState}
        validateOnMount={false}
        validationSchema={validationSchema}
        context={formState}
        onSubmit={(values) => {
          console.log('Form Values:', values);
          handleIntakeformSave(values);
        }}
        enableReinitialize
      >
        {({ values, handleSubmit, errors, touched, setFieldValue }) => (
          <Form>
            <Box className="pt-20 pb-20 text-align-left">
              <Grid2 container className="align-items-center">
                <Grid2
                  size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                  className="text-align-left display-flex"
                >
                  <CustomIconButton
                    id="back-button"
                    icon={<BackIcon />}
                    onClick={() => {
                      navigate(INTAKE_ROUTE_PATH, { replace: true });
                    }}
                  />
                  <CustomTypography
                    className="pl-8"
                    id="intake-form-title"
                    variant="h4"
                  >
                    {(intakeFormId && loadForm === 'copy') || !intakeFormId
                      ? 'Create Intake Form'
                      : 'Edit Intake Form'}
                  </CustomTypography>
                  <CustomTypography
                    className="pl-12"
                    variant="h4"
                    id="partner-business-id"
                  >
                    {intakeFormId && !loadForm
                      ? `(${formState.intakeFormBusinessId || ''})`
                      : ''}
                  </CustomTypography>
                </Grid2>

                <Grid2
                  size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                  className="text-align-right"
                >
                  {!intakeFormId && (
                    <CustomButton
                      id="load-form-button"
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        handleOpenIntakeForm(true);
                        handleFetchIntakeFormList();
                      }}
                    >
                      Load Form
                    </CustomButton>
                  )}
                </Grid2>
              </Grid2>
            </Box>
            <Box className="pb-32 text-align-left">
              <Box className="pb-12">
                <CustomTypography
                  id="intake-form-title"
                  variant="body3"
                  color="textDisabled"
                >
                  Intake Form Name&nbsp;<span className="color-red">*</span>
                </CustomTypography>
              </Box>
              <Box className="width-30">
                <CustomTextField
                  id="intake-form-name"
                  variant="outlined"
                  size="small"
                  placeholder="Enter Intake Form Name"
                  value={values.intakeFormName}
                  onChange={(event) => {
                    setFieldValue('intakeFormName', event.target.value);
                  }}
                  name="intakeFormName"
                ></CustomTextField>
                {errors?.intakeFormName && touched?.intakeFormName && (
                  <CustomTypography
                    id="validate-diagnosis"
                    color="error"
                    variant="body3"
                    className="pt-8"
                  >
                    {errors.intakeFormName}
                  </CustomTypography>
                )}
              </Box>
            </Box>
            <Box className="pt-32">
              <Grid2 container className="align-items-center">
                <Grid2
                  size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                  className="text-align-left"
                >
                  <Box>
                    <SessionFieldsComponent
                      sessionFields={values.sessionFields}
                      setSessionFields={(newRows) =>
                        setFieldValue(
                          'sessionFields',
                          typeof newRows === 'function'
                            ? newRows(values.sessionFields)
                            : newRows
                        )
                      }
                      errors={errors.sessionFields}
                      touched={touched.sessionFields}
                    />
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
            <Box className="pt-32">
              <Grid2 container className="align-items-center">
                <Grid2
                  size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                  className="text-align-left"
                >
                  <Box>
                    <AdditionalSessionFields
                      additinalSessionFields={values.additionalSessionFields}
                      setAdditinalSessionFields={(newRows) =>
                        setFieldValue(
                          'additionalSessionFields',
                          typeof newRows === 'function'
                            ? newRows(values.additionalSessionFields)
                            : newRows
                        )
                      }
                      AdditionalSessionFieldsTypeddl={
                        values.additionalSessionFieldsTypeddl
                      }
                      errors={errors.additionalSessionFields}
                      touched={touched.additionalSessionFields}
                    />
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
            <Box className="pt-32">
              <Grid2 container className="align-items-center">
                <Grid2
                  size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                  className="text-align-left"
                >
                  <Box>
                    <FacilityFieldsComponent
                      facilityFields={values.facilityFields}
                      setFacilityFields={(newFields) =>
                        setFieldValue(
                          'facilityFields',
                          typeof newFields === 'function'
                            ? newFields(values.facilityFields)
                            : newFields
                        )
                      }
                      facilityFieldsddl={values.facilityFieldsddl}
                    />
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
            <Box className="pt-32 pb-24">
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <Box className="display-flex justify-end">
                    <Box className="pr-12">
                      <CustomButton
                        id="cancel-button"
                        onClick={() => {
                          navigate(INTAKE_ROUTE_PATH);
                        }}
                        variant="outlined"
                      >
                        Cancel
                      </CustomButton>
                    </Box>
                    <Box>
                      <CustomButton
                        id="btnSave-intake-form"
                        color="primary"
                        variant="contained"
                        onClick={() => {
                          setFormSubmitAction(
                            (intakeFormId && loadForm === 'copy') ||
                              !intakeFormId
                              ? 'Save'
                              : 'Update'
                          );
                          handleSubmit();
                        }}
                        disabled={isSaving}
                      >
                        {buttonText}
                      </CustomButton>
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
          </Form>
        )}
      </Formik>

      {/* popup */}

      <Formik
        key={openIntakeForm ? 'open' : 'closed'}
        initialValues={{ selectedIntakeForm: null }}
        validationSchema={validationSchemaForddl}
        validateOnMount={false}
        enableReinitialize={true}
        onSubmit={(values) => {
          handleIntakeFormChange(values.selectedIntakeForm);
          handleOpenIntakeForm(false);
          handleOpenConfirmDialog(true);
        }}
      >
        {({ setFieldValue, handleSubmit, errors, touched, resetForm }) => {
          return (
            <Form>
              <Box>
                <CustomDialog
                  id="intake-form-dialog"
                  open={openIntakeForm}
                  onClose={() => {
                    handleOpenIntakeForm(true);
                    resetForm({ values: { selectedIntakeForm: null } });
                  }}
                  disableEscapeKeyDown={true}
                >
                  <CustomDialogContent className="intake-dialog-width">
                    <Box className="pb-8">
                      <CustomTypography id="intake-form-title" variant="h4">
                        Select an Intake Form
                      </CustomTypography>
                    </Box>
                    <Box>
                      <CustomTypography
                        id="intake-form-text"
                        variant="body3"
                        color="textDisabled"
                      >
                        Please choose an intake form from the dropdown below.
                        Once selected, confirm your choice to proceed with that
                        form.
                      </CustomTypography>
                    </Box>

                    <Box className="mt-32 mb-8">
                      <CustomAutoCompleteSingleSelection
                        placeholder="Select Intake Form Name"
                        options={intakeFormOptions}
                        value={selectedIntakeForm}
                        onChange={(value) => {
                          setFieldValue('selectedIntakeForm', value);
                        }}
                        size="small"
                      />

                      {errors?.selectedIntakeForm &&
                        touched?.selectedIntakeForm && (
                          <CustomTypography
                            id="validate-diagnosis"
                            color="error"
                            variant="body3"
                            className="pt-8"
                          >
                            {errors.selectedIntakeForm}
                          </CustomTypography>
                        )}
                    </Box>
                  </CustomDialogContent>

                  <CustomDialogActions>
                    <Grid2 container>
                      <Grid2 className="mr-8">
                        <CustomButton
                          id="btnYes"
                          color="primary"
                          variant="outlined"
                          onClick={() => {
                            handleOpenIntakeForm(false);
                            resetForm({ values: { selectedIntakeForm: null } });
                          }}
                        >
                          Cancel
                        </CustomButton>
                      </Grid2>
                      <Grid2>
                        <CustomButton
                          id="btnConfirm-complete"
                          color="primary"
                          variant="contained"
                          onClick={() => {
                            handleSubmit();
                          }}
                        >
                          Confirm
                        </CustomButton>
                      </Grid2>
                    </Grid2>
                  </CustomDialogActions>
                </CustomDialog>
              </Box>
            </Form>
          );
        }}
      </Formik>
      <Formik
        initialValues={{}}
        validateOnMount={false}
        onSubmit={() => {
          navigate(`${NEW_INTAKE_ROUTE_PATH}/${selectedIntakeForm?.id}`, {
            replace: true,
            state: {
              loadForm: 'copy',
            },
          });
          handleOpenConfirmDialog(false);
        }}
      >
        {({ handleSubmit }) => (
          <Form>
            <Box>
              <CustomDialog
                id="confirm-dialog"
                open={openConfirmDialog}
                onClose={() => {
                  handleOpenConfirmDialog(true);
                }}
                disableEscapeKeyDown={true}
              >
                <CustomDialogContent className="intake-dialog-width ">
                  <Box className="pb-8">
                    <CustomTypography id="confirm-dialog-title" variant="h4">
                      Confirm Action
                    </CustomTypography>
                  </Box>
                  <Box>
                    <CustomTypography
                      id="confirm-dialog-text"
                      variant="body3"
                      color="textDisabled"
                    >
                      Loading this intake form will overwrite any progress you
                      may have made on the current form. Do you still wish to
                      proceed?
                    </CustomTypography>
                  </Box>
                </CustomDialogContent>

                <CustomDialogActions>
                  <Grid2 container>
                    <Grid2 className="mr-8">
                      <CustomButton
                        id="btnYes"
                        color="primary"
                        variant="outlined"
                        onClick={() => {
                          handleOpenConfirmDialog(false);
                          setSelectedIntakeForm(null);
                        }}
                      >
                        Cancel
                      </CustomButton>
                    </Grid2>
                    <Grid2>
                      <CustomButton
                        id="btnConfirm-complete"
                        color="primary"
                        variant="contained"
                        onClick={() => {
                          handleOpenConfirmDialog(false);
                          handleSubmit();
                        }}
                      >
                        Confirm
                      </CustomButton>
                    </Grid2>
                  </Grid2>
                </CustomDialogActions>
              </CustomDialog>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default IntakeForm;
